import {
	Component,
	ContentChildren,
	QueryList,
	Input,
	AfterContentInit,
	AfterViewInit,
	Output,
	EventEmitter,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { AspectEvaluationComponent } from '../aspect-evaluation/aspect-evaluation.component';
import { ValidityOptions } from '@app/models/evaluation.model';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { Subscription } from 'rxjs';

@Component({
	selector: 'app-aspect-evaluation-group',
	standalone: true,
	imports: [CommonModule, MatSlideToggleModule],
	templateUrl: './aspect-evaluation-group.component.html',
	styleUrls: ['./aspect-evaluation-group.component.scss'],
})
export class AspectEvaluationGroupComponent implements AfterContentInit, AfterViewInit {
	@Input() allowMultipleSectionsOpen = false;
	@Input() showAllAspects = false;
	@Output() activeAspectChange = new EventEmitter<string[]>();
	@Output() finished = new EventEmitter();
	@Output() initialized = new EventEmitter();
	@Output() showPreviouslyCompleted = new EventEmitter<boolean>();
	@ContentChildren(AspectEvaluationComponent) aspects!: QueryList<AspectEvaluationComponent>;

	completed: number = 0;
	private subscriptions: Subscription[] = [];

	private aspectSubscriptions: Map<AspectEvaluationComponent, Subscription[]> = new Map();
	private changesSubscription?: Subscription;

	ngAfterViewInit() {
		setTimeout(() => {
			this.initialized.emit();
		}, 0);
	}

	ngAfterContentInit() {
		// Setup initial aspects
		this.setupAspects(this.aspects.toArray());

		// Subscribe to future changes
		this.changesSubscription = this.aspects.changes.subscribe(
			(changes: QueryList<AspectEvaluationComponent>) => {
				// Clean up subscriptions for removed aspects
				this.cleanupRemovedAspects(changes.toArray());
				// Setup new aspects
				this.setupAspects(changes.toArray());
			},
		);
	}

	ngOnDestroy() {
		this.cleanupAllSubscriptions();
		this.changesSubscription?.unsubscribe();
	}

	private cleanupRemovedAspects(currentAspects: AspectEvaluationComponent[]) {
		// Get all aspects that we have subscriptions for
		const aspectsWithSubs = Array.from(this.aspectSubscriptions.keys());

		// Find aspects that are no longer present
		const removedAspects = aspectsWithSubs.filter(aspect => !currentAspects.includes(aspect));

		// Cleanup subscriptions for removed aspects
		removedAspects.forEach(aspect => {
			const subs = this.aspectSubscriptions.get(aspect);
			subs?.forEach(sub => sub.unsubscribe());
			this.aspectSubscriptions.delete(aspect);
		});
	}

	private cleanupAllSubscriptions() {
		this.aspectSubscriptions.forEach(subs => {
			subs.forEach(sub => sub.unsubscribe());
		});
		this.aspectSubscriptions.clear();
	}

	private setupAspects(aspects: AspectEvaluationComponent[]) {
		aspects.forEach((aspect, index) => {
			// Skip if aspect already has subscriptions
			if (this.aspectSubscriptions.has(aspect)) {
				return;
			}

			aspect.isFirst = index === 0;
			aspect.isLast = index === aspects.length - 1;

			const subscriptions: Subscription[] = [];

			// Add aspect change subscription
			subscriptions.push(
				aspect.aspectChange.subscribe(() => {
					this.updateStatusSummary();
					if (this.completed === this.aspects.length) {
						if (this.validate()) {
							this.finished.emit();
						}
					}
				}),
			);

			// Add accordion toggle subscription
			subscriptions.push(
				aspect.accordionToggle.subscribe(() => {
					if (!aspect.isCollapsed) {
						this.handleAspectToggle(aspect);
					}
					const openFactorIds = this.aspects
						.filter(aspect => !aspect.isCollapsed)
						.map(aspect => aspect.aspect.factorId);

					// Don't highlight the aspect text if the aspect cannot be evaluated
					this.activeAspectChange.emit(aspect.canEvaluateAspect ? openFactorIds : []);
				}),
			);

			// Add navigation subscription
			subscriptions.push(
				aspect.navigate.subscribe(direction => {
					if (direction !== 'finished') {
						const currentIndex = this.getAspectIndex(aspect);
						const targetIndex =
							direction === 'next' ? currentIndex + 1 : currentIndex - 1;
						if (targetIndex >= 0 && targetIndex < this.aspects.length) {
							aspect.setCollapsed(true);
							const targetAspect = this.getAspectByIndex(targetIndex);
							if (targetAspect) {
								targetAspect.setCollapsed(false);
							}
						}
					} else {
						if (this.completed === this.aspects.length) {
							this.finished.emit();
						}
					}
				}),
			);

			// Store subscriptions for this aspect
			this.aspectSubscriptions.set(aspect, subscriptions);
		});

		// Update first/last status for all aspects
		aspects.forEach((aspect, index) => {
			aspect.isFirst = index === 0;
			aspect.isLast = index === aspects.length - 1;
		});

		// Initial status summary
		this.updateStatusSummary();
	}

	onToggleChange(event: any) {
		this.showAllAspects = event.checked;
		this.showPreviouslyCompleted.emit(event.checked);
	}

	private getAspectIndex(aspect: AspectEvaluationComponent): number {
		return this.aspects.toArray().indexOf(aspect);
	}

	private getAspectByIndex(index: number): AspectEvaluationComponent | null {
		const aspectsArray = this.aspects.toArray();
		return aspectsArray[index] || null;
	}

	private handleAspectToggle(toggledAspect: AspectEvaluationComponent) {
		// Scroll selected aspect into view
		setTimeout(() => {
			toggledAspect.scrollIntoViewIfOpen();
		}, 300);
		if (!this.allowMultipleSectionsOpen) {
			// Close other aspects when one is opened
			this.aspects.forEach(aspect => {
				if (aspect !== toggledAspect) {
					aspect.setCollapsed(true);
				}
			});
		}
	}

	private updateStatusSummary() {
		this.completed = this.aspects.reduce((prev, curr) => {
			if (curr.getStatus() !== 'pending') {
				if (this.showAllAspects || curr.canEvaluateAspect) {
					return (prev += 1);
				}
			}
			return prev;
		}, 0);
	}

	public validate(options: ValidityOptions = {}) {
		return this.aspects.reduce((validSoFar, currAspect) => {
			currAspect.reportValidity(options);
			return validSoFar && currAspect.isValid();
		}, true);
	}
}
